import axios from 'axios';
import './beacon';

let initialized = false;
const bootstrapPromises = new Map();
const bootstrapConfigs = new Map();

const currentHost = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.host || '';
};

const currentPageUrl = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.href;
};

const buildBootstrapUrl = (bootstrapUrl) => {
    const url = new URL(bootstrapUrl, currentPageUrl() || undefined);
    url.searchParams.set('host', currentHost());
    return url.toString();
};

const permitted = control => !control?.signal?.aborted && (!control?.allowed || control.allowed());

const ensureInitialized = (config, control) => {
    if (!permitted(control) || !config || !config.enabled || !config.authToken || initialized || !window.HCBeacon) {
        return;
    }

    window.HCBeacon.init({
        endpoint: config.endpoint,
        authToken: config.authToken,
        enabled: config.enabled,
        isAllowed: () => permitted(control),
        autoTrackPageView: false,
        autoReadProgress: false,
        autoSearchInstrumentation: false,
        autoExternalLinkTracking: false,
    });

    initialized = true;
};

const bootstrapHostBeacon = async (bootstrapUrl, control) => {
    if (!permitted(control)) return null;
    if (bootstrapConfigs.has(bootstrapUrl)) {
        return bootstrapConfigs.get(bootstrapUrl);
    }

    if (!bootstrapPromises.has(bootstrapUrl)) {
        bootstrapPromises.set(bootstrapUrl, axios.get(buildBootstrapUrl(bootstrapUrl), { signal: control?.signal }).then((resp) => {
            if (!permitted(control)) return null;
            const config = resp.data;
            bootstrapConfigs.set(bootstrapUrl, config);
            ensureInitialized(config, control);
            return config;
        }).catch((error) => {
            bootstrapPromises.delete(bootstrapUrl);
            throw error;
        }));
    }

    const config = await bootstrapPromises.get(bootstrapUrl);
    if (config && permitted(control)) {
        bootstrapConfigs.set(bootstrapUrl, config);
        ensureInitialized(config, control);
        return config;
    }

    return null;
};

const getBootstrapConfig = (bootstrapUrl) => {
    if (bootstrapConfigs.has(bootstrapUrl)) {
        return bootstrapConfigs.get(bootstrapUrl);
    }

    return null;
};

const appendSessionTransfer = (url, bootstrapUrl, journeyId = '', sessionId = '') => {
    const config = getBootstrapConfig(bootstrapUrl);
    const journeyTransferParam = config?.journeyTransferParam || 'hc_jid';
    const sessionTransferParam = config?.sessionTransferParam || 'hc_sid';
    const resolvedJourneyId = journeyId || (window.HCBeacon?.getJourneyId ? window.HCBeacon.getJourneyId() : '');
    const resolvedSessionId = sessionId || (window.HCBeacon?.getSessionId ? window.HCBeacon.getSessionId() : '');
    if (!url || (!resolvedJourneyId && !resolvedSessionId)) {
        return url;
    }

    try {
        const parsed = new URL(url, currentPageUrl() || undefined);
        if (resolvedJourneyId) {
            parsed.searchParams.set(journeyTransferParam, resolvedJourneyId);
        }
        if (resolvedSessionId) {
            parsed.searchParams.set(sessionTransferParam, resolvedSessionId);
        }
        return parsed.toString();
    } catch (error) {
        return url;
    }
};

const trackWithBootstrap = async (bootstrapUrl, method, payload = {}, control) => {
    const config = await bootstrapHostBeacon(bootstrapUrl, control);
    if (!permitted(control)) return null;
    if (!config || !config.enabled || !window.HCBeacon || typeof window.HCBeacon[method] !== 'function') {
        return config;
    }

    window.HCBeacon[method]({
        ...payload,
        ...buildSurfaceContext(config, payload),
    });

    return config;
};

const buildSurfaceContext = (config, overrides = {}) => ({
    surface: overrides.surface || config.surface || '',
    widgetId: overrides.widgetId || overrides.widget_id || config.widgetId || '',
    faqSectionId: overrides.faqSectionId || overrides.faq_section_id || config.faqSectionId || '',
    embedHost: overrides.embedHost || overrides.embed_host || currentHost(),
    pageUrl: overrides.pageUrl || overrides.page_url || currentPageUrl(),
    articleViewType: overrides.articleViewType || overrides.article_view_type || '',
    transitionTarget: overrides.transitionTarget || overrides.transition_target || '',
});

const getSessionId = async (bootstrapUrl, control) => {
    await bootstrapHostBeacon(bootstrapUrl, control);
    if (!permitted(control)) return "";
    return window.HCBeacon?.getSessionId ? window.HCBeacon.getSessionId() : '';
};

const getJourneyId = async (bootstrapUrl, control) => {
    await bootstrapHostBeacon(bootstrapUrl, control);
    if (!permitted(control)) return "";
    return window.HCBeacon?.getJourneyId ? window.HCBeacon.getJourneyId() : '';
};

export {
    appendSessionTransfer,
    bootstrapHostBeacon,
    buildSurfaceContext,
    currentHost,
    currentPageUrl,
    getBootstrapConfig,
    getJourneyId,
    getSessionId,
    trackWithBootstrap,
};
